import React, { useState, useEffect, useRef } from 'react';
import { ReactGridLayout, noCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { API_BASE, authHeaders } from '../../constants/api';
import { WIDGET_TYPES, tbWidgetToEditor, editorWidgetToTb } from './widgetTypes';
import WidgetSettingsPanel from './WidgetSettingsPanel';
import AddWidgetDialog from './AddWidgetDialog';
import './DashboardEditor.css';
import { Hinzufügen, Speichern, Abbrechen } from '../../icons';

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

function parseConfiguration(configuration) {
  const widgets = configuration?.widgets || {};
  const states = configuration?.states || {};
  const defaultState = states.default || Object.values(states)[0] || {};
  const layouts = defaultState.layouts || {};
  const mainLayout = layouts.main || Object.values(layouts)[0] || {};
  const layoutWidgets = mainLayout?.widgets || {};
  const gridSettings = mainLayout?.gridSettings || {};

  const editorWidgets = [];
  for (const [wId, wConfig] of Object.entries(widgets)) {
    const lay = layoutWidgets[wId] || {};
    editorWidgets.push(tbWidgetToEditor(wId, wConfig, lay));
  }

  return { editorWidgets, gridSettings };
}


function DashboardEditor({ configuration, dashboardId, token, onClose, onDirtyChange, saveOverride, extraSaveDisabled, saveLabel, saveIcon, toolbarExtra }) {
  const [widgets, setWidgets] = useState([]);
  const [gridSettings, setGridSettings] = useState({ columns: 24, margin: 10, rowHeight: 50 });
  const [selectedId, setSelectedId] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const [undoStack, setUndoStack] = useState([]);
  const MAX_UNDO = 30;

  function pushUndo(currentWidgets) {
    setUndoStack(prev => {
      const next = [...prev, JSON.parse(JSON.stringify(currentWidgets))];
      return next.length > MAX_UNDO ? next.slice(next.length - MAX_UNDO) : next;
    });
  }

  function handleUndo() {
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const next = [...prev];
      const restored = next.pop();
      setWidgets(restored);
      setDirty(true);
      return next;
    });
  }

  const gridContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setContainerWidth(w);
      }
    });
    ro.observe(el);
    // Initial measurement
    if (el.offsetWidth > 0) setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const { editorWidgets, gridSettings: gs } = parseConfiguration(configuration);
    setWidgets(editorWidgets);
    const margin = gs.margin ?? 10;
    const maxRow = editorWidgets.reduce((m, w) => Math.max(m, w.layout.y + w.layout.h), 0);
    const dynamicRowHeight = maxRow > 0 ? Math.max(10, Math.min(40, Math.round(550 / maxRow))) : 25;
    setGridSettings({
      columns: 24,
      margin,
      rowHeight: dynamicRowHeight,
    });
  }, [configuration]);

  const layout = widgets.map(w => ({
    i: w.id,
    x: w.layout.x,
    y: w.layout.y,
    w: w.layout.w,
    h: w.layout.h,
    minW: 2,
    minH: 2,
  }));

  function handleLayoutItemChange(layout, oldItem, newItem) {
    setWidgets(prev => {
      pushUndo(prev);
      return prev.map(w => {
        if (w.id !== newItem.i) return w;
        return { ...w, layout: { x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h } };
      });
    });
    setDirty(true);
  }

  function updateWidget(id, changes) {
    setWidgets(prev => {
      pushUndo(prev);
      return prev.map(w => w.id === id ? { ...w, ...changes } : w);
    });
    setDirty(true);
  }

  function deleteWidget(id) {
    setWidgets(prev => {
      pushUndo(prev);
      return prev.filter(w => w.id !== id);
    });
    if (selectedId === id) setSelectedId(null);
    setDirty(true);
  }

  function addWidget(widgetType) {
    const typeDef = WIDGET_TYPES[widgetType];
    if (!typeDef) return;

    const defaults = {};
    for (const f of typeDef.fields) {
      defaults[f.key] = f.default;
    }

    const maxY = widgets.reduce((m, w) => Math.max(m, w.layout.y + w.layout.h), 0);

    const newWidget = {
      id: generateId(),
      widgetType,
      title: typeDef.label,
      deviceId: '',
      keys: typeDef.fixedKey ? [typeDef.fixedKey] : [],
      keyUnits: {},
      keyLabels: {},
      settings: defaults,
      stateValues: typeDef.hasStateValues ? [{ label: '', value: '' }, { label: '', value: '' }] : [],
      layout: { x: 0, y: maxY, w: 6, h: 4 },
    };

    setWidgets(prev => [...prev, newWidget]);
    setSelectedId(newWidget.id);
    setShowAddDialog(false);
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');

    try {
      const newWidgets = {};
      const newLayoutWidgets = {};

      for (const w of widgets) {
        const tbWidget = editorWidgetToTb(w);
        if (!tbWidget) continue;
        tbWidget.id = w.id;
        tbWidget.sizeX = w.layout.w;
        tbWidget.sizeY = w.layout.h;
        tbWidget.row = w.layout.y;
        tbWidget.col = w.layout.x;
        newWidgets[w.id] = tbWidget;
        newLayoutWidgets[w.id] = {
          col: w.layout.x,
          row: w.layout.y,
          sizeX: w.layout.w,
          sizeY: w.layout.h,
        };
      }

      if (saveOverride) {
        const builtConfig = {
          widgets: newWidgets,
          states: {
            default: {
              name: 'default',
              root: true,
              layouts: {
                main: {
                  widgets: newLayoutWidgets,
                  gridSettings: { columns: 24, margin: gridSettings.margin },
                },
              },
            },
          },
        };
        await saveOverride(builtConfig);
        setDirty(false);
        return; 
      }

      const getRes = await fetch(`${API_BASE}/dashboard/${dashboardId}`, {
        headers: authHeaders(token),
      });
      if (!getRes.ok) throw new Error('Dashboard konnte nicht geladen werden');
      const dashData = await getRes.json();

      const config = dashData.configuration || {};
      config.widgets = newWidgets;

      // States/Layout aktualisieren
      const states = config.states || {};
      const defaultKey = Object.keys(states)[0] || 'default';
      if (!states[defaultKey]) states[defaultKey] = {};
      const layouts = states[defaultKey].layouts || {};
      const mainKey = Object.keys(layouts)[0] || 'main';
      if (!layouts[mainKey]) layouts[mainKey] = {};
      layouts[mainKey].widgets = newLayoutWidgets;
      states[defaultKey].layouts = layouts;
      config.states = states;

      dashData.configuration = config;

      const putRes = await fetch(`${API_BASE}/dashboard`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(dashData),
      });

      if (!putRes.ok) {
        const errBody = await putRes.text().catch(() => '');
        console.error('[DashboardEditor] PUT failed:', putRes.status, errBody);
        throw new Error(`HTTP ${putRes.status}: ${errBody.substring(0, 200)}`);
      }

      setDirty(false);
      onClose(true); // true = gespeichert → Seite soll neu laden
    } catch (err) {
      console.error('[DashboardEditor] Save error:', err);
      setSaveError(err.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (dirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose(false);
    }
  }

  function confirmDiscard() {
    setShowDiscardConfirm(false);
    setDirty(false);
    onClose(false);
  }

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const selectedWidget = widgets.find(w => w.id === selectedId);


  const gridConfig = {
    cols: gridSettings.columns,
    rowHeight: gridSettings.rowHeight,
    margin: [gridSettings.margin, gridSettings.margin],
    containerPadding: [gridSettings.margin, gridSettings.margin],
  };

  const dragConfig = {
    enabled: true,
    handle: '.editor-widget-drag-handle',
  };

  const resizeConfig = {
    enabled: true,
    handles: ['e', 'w', 'se', 'sw'],
  };

  return (
    <div className="dashboard-editor">
      {/* Toolbar */}
      <div className="dashboard-editor-toolbar">
        {toolbarExtra && (
          <div className="dashboard-editor-toolbar-left">
            {toolbarExtra}
          </div>
        )}
        <div className="dashboard-editor-toolbar-right">
          {saveError && <span className="editor-save-error">{saveError}</span>}
          {dirty && <span className="editor-unsaved-badge">Ungespeichert</span>}
          <button className="editor-toolbar-btn editor-toolbar-btn--ghost" title="Widget hinzufügen" onClick={() => setShowAddDialog(true)}>
            <span className="btn-icon" aria-hidden="true"><Hinzufügen /></span>
            <span className="btn-label"> Widget hinzufügen</span>
          </button>
          <button
            className="editor-toolbar-btn editor-toolbar-btn--ghost"
            onClick={handleUndo}
            disabled={!undoStack.length}
            title="Rückgängig"
          >
            <span className="btn-icon" aria-hidden="true">↩</span>
            <span className="btn-label"> Rückgängig</span>
          </button>
          <button
            className="editor-toolbar-btn editor-toolbar-btn--primary"
            onClick={handleSave}
            disabled={saving || !!extraSaveDisabled}
            title={saveLabel || 'Speichern'}
          >
            <span className="btn-icon" aria-hidden="true">{saveIcon || <Speichern />}</span>
            <span className="btn-label"> {saving ? 'Speichern…' : (saveLabel || 'Speichern')}</span>
          </button>
          <button className="editor-toolbar-btn editor-toolbar-btn--secondary" onClick={handleCancel} disabled={saving} title="Abbrechen">
            <span className="btn-icon" aria-hidden="true"><Abbrechen /></span>
            <span className="btn-label"> Abbrechen</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="dashboard-editor-content">
        {/* Grid */}
        <div className={`dashboard-editor-grid${selectedWidget ? ' dashboard-editor-grid--with-panel' : ''}`} ref={gridContainerRef}>
          <ReactGridLayout
            className="editor-grid-layout"
            layout={layout}
            width={containerWidth}
            gridConfig={gridConfig}
            dragConfig={dragConfig}
            resizeConfig={resizeConfig}
            compactor={noCompactor}
            onDragStop={handleLayoutItemChange}
            onResizeStop={handleLayoutItemChange}
          >
            {widgets.map((w, idx) => {
              const typeDef = WIDGET_TYPES[w.widgetType];
              const isSelected = w.id === selectedId;
              return (
                <div
                  key={w.id}
                  className={`editor-widget-card${isSelected ? ' editor-widget-card--selected' : ''}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`${typeDef?.label || 'Widget'}: ${w.title || '(Kein Titel)'}. ${isSelected ? 'Ausgewählt.' : 'Eingabetaste zum Auswählen.'}`}
                  aria-pressed={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(w.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedId(w.id);
                    } else if (e.key === 'Delete' || e.key === 'Backspace') {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteWidget(w.id);
                    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = idx + 1 < widgets.length ? idx + 1 : 0;
                      const nextEl = e.currentTarget.parentElement?.querySelector(`[data-widget-idx="${next}"]`);
                      if (nextEl) nextEl.focus();
                    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = idx - 1 >= 0 ? idx - 1 : widgets.length - 1;
                      const prevEl = e.currentTarget.parentElement?.querySelector(`[data-widget-idx="${prev}"]`);
                      if (prevEl) prevEl.focus();
                    }
                  }}
                  data-widget-idx={idx}
                >
                  <div className="editor-widget-drag-handle">
                    <span className="editor-widget-drag-icon">⠿</span>
                    <span className="editor-widget-type-badge">
                      {typeDef?.label}
                    </span>
                  </div>
                  <div className="editor-widget-title">{w.title || '(Kein Titel)'}</div>
                  <div className="editor-widget-info">
                    {w.keys.length ? w.keys.join(', ') : 'Keine Keys'}
                  </div>
                  <button
                    className="editor-widget-delete"
                    onClick={(e) => { e.stopPropagation(); deleteWidget(w.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
                    aria-label="Widget entfernen"
                    title="Widget entfernen"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </ReactGridLayout>
          {!widgets.length && (
            <div className="editor-grid-empty">
              Keine Widgets vorhanden. Klicke auf "Widget hinzufügen" um zu starten.
            </div>
          )}
        </div>

        {/* Settings Panel */}
        {selectedWidget && (
          <WidgetSettingsPanel
            widget={selectedWidget}
            token={token}
            onChange={(changes) => updateWidget(selectedWidget.id, changes)}
            onLayoutChange={(newLayout) => updateWidget(selectedWidget.id, { layout: newLayout })}
            onDelete={() => deleteWidget(selectedWidget.id)}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>

      {/* Add Widget Dialog */}
      {showAddDialog && (
        <AddWidgetDialog
          onSelect={addWidget}
          onClose={() => setShowAddDialog(false)}
          token={token}
        />
      )}

      {/* Discard Confirmation Modal */}
      {showDiscardConfirm && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="discard-confirm-title">
          <div className="modal">
            <h2 id="discard-confirm-title">
              Änderungen verwerfen?
            </h2>
            <p>
              Es gibt ungespeicherte Änderungen. Möchten Sie diese wirklich verwerfen?
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={confirmDiscard}>
                Verwerfen
              </button>
              <button className="modal-secondary" onClick={() => setShowDiscardConfirm(false)}>
                ✕ Zurück
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardEditor;
